import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import { TitleDivider } from "areas/cart/components"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import { cn } from "styles/utils"

import ExclusiveAddon from "./addon"

const selectPremiumGiftOffers = createSelector(
  [(state) => state.cart.addons, (state) => state.cart.premium_gift_offers],
  (addons, premiumGiftOffers) => {
    const filteredAddons = addons.filter((addon) =>
      ["BUNDLE", "GIFT_CARD"].includes(addon.addon_type)
    )

    return premiumGiftOffers.map((offer) => ({
      addons: filteredAddons.filter((addon) => addon.addon_code === offer.code),
      ...offer,
    }))
  }
)

const ExclusiveAddons = ({
  disableActions,
  setAddonInModal,
  toggleAddonModal,
  toggleDisableActions,
  upsellKit,
}) => {
  const premiumGiftOffers = useSelector(selectPremiumGiftOffers)

  const filteredPremiumGiftOffers = useMemo(
    () => (premiumGiftOffers || []).filter((item) => item.code !== upsellKit),
    [upsellKit, premiumGiftOffers]
  )

  const carouselActive = filteredPremiumGiftOffers?.length > 4

  if (!filteredPremiumGiftOffers.length) return null

  return (
    <>
      <section
        aria-labelledby="cart-exclusive-addons"
        className="desktop:mt-40 tablet:px-0 border-b-grey-20 mt-24 border-b px-16 pb-24"
      >
        <TitleDivider className="tablet:before:border-b tablet:after:border-b tablet:before:flex-1 tablet:before:mr-10">
          <h2
            className="text-20 desktop:text-26 font-bold"
            id="cart-exclusive-addons"
          >
            Exclusive Add-on Deals
          </h2>
        </TitleDivider>
        <p className="text-15 desktop:text-18 desktop:mb-24 desktop:mt-8 mb-20 mt-2 text-center font-medium">
          Save on these Exclusive Add-on Deals!
        </p>
        <Carousel
          className="tablet:mx-0 -mx-16"
          description="Exclusive Add-on Deals"
          disableSkip={!carouselActive}
          opts={{
            active: true,
            breakpoints: {
              "(min-width: 768px)": { active: carouselActive },
            },
            dragFree: true,
            loop: false,
            slidesToScroll: "auto",
          }}
        >
          <CarouselContent
            className={cn(
              "desktop:-ml-20 tablet:mr-0 tablet:pl-0 -ml-10 mr-16 pl-16",
              !carouselActive && "tablet:justify-center"
            )}
          >
            {filteredPremiumGiftOffers.map((item, i) => (
              <CarouselItem
                className="tablet:basis-1/4 desktop:pl-20 basis-9/20 pl-10"
                key={`cart-exclusive-addons-${item.code}-${i}`}
              >
                <ExclusiveAddon
                  disableActions={disableActions}
                  item={item}
                  setAddonInModal={setAddonInModal}
                  toggleAddonModal={toggleAddonModal}
                  toggleDisableActions={toggleDisableActions}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious
            className={cn(
              "inline-flex",
              carouselActive ? "desktop:inline-flex" : "desktop:hidden"
            )}
          />
          <CarouselNext
            className={cn(
              "inline-flex",
              carouselActive ? "desktop:inline-flex" : "desktop:hidden"
            )}
          />
        </Carousel>
      </section>
    </>
  )
}

ExclusiveAddons.propTypes = {
  disableActions: PropTypes.object,
  setAddonInModal: PropTypes.func,
  toggleAddonModal: PropTypes.func,
  toggleDisableActions: PropTypes.func,
  upsellKit: PropTypes.string,
}

export default ExclusiveAddons
